import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ClipboardList, EyeIcon } from "lucide-react";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

export type ChecklistExecutionHistoryRow = {
  id: string;
  checklistId: string;
  checklistTitle: string;
  departmentId: string | null;
  departmentName: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  status: "in_progress" | "completed" | "cancelled";
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
};

const statusLabels: Record<ChecklistExecutionHistoryRow["status"], string> = {
  in_progress: "ກຳລັງດຳເນີນການ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
};

const statusVariant: Record<
  ChecklistExecutionHistoryRow["status"],
  "default" | "secondary" | "outline" | "destructive"
> = {
  in_progress: "secondary",
  completed: "default",
  cancelled: "destructive",
};

type ChecklistExecutionHistoryTableProps = {
  data: ChecklistExecutionHistoryRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function ChecklistExecutionHistoryTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
}: ChecklistExecutionHistoryTableProps) {
  const nav = useNavigate();

  const columns: TanstackReactTable.ColumnDef<ChecklistExecutionHistoryRow>[] =
    [
      {
        id: "startedAt",
        header: "ເວລາເລີ່ມ",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDateTime(row.original.startedAt)}
          </span>
        ),
        size: 160,
      },
      {
        id: "checklistTitle",
        header: "Checklist",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.checklistTitle}</span>
        ),
        size: 200,
      },
      {
        id: "departmentName",
        header: "ພະແນກ",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.departmentName ?? "—"}</span>
        ),
        size: 140,
      },
      {
        id: "user",
        header: "ພະນັກງານ",
        cell: ({ row }) => (
          <div>
            <div className="text-sm">{row.original.userName}</div>
            <div className="text-muted-foreground text-xs">
              {row.original.userEmail}
            </div>
          </div>
        ),
        size: 200,
      },
      {
        id: "status",
        header: "ສະຖານະ",
        cell: ({ row }) => {
          const v = row.original.status;
          return <Badge variant={statusVariant[v]}>{statusLabels[v]}</Badge>;
        },
        size: 110,
      },
      {
        id: "completedAt",
        header: "ເວລາສຳເລັດ",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.completedAt
              ? formatDateTime(row.original.completedAt)
              : "—"}
          </span>
        ),
        size: 160,
      },
      {
        id: "actions",
        size: 72,
        cell: ({ row }) => (
          <RowActions
            actions={[
              {
                label: "ລາຍລະອຽດການບັນທຶກ",
                icon: <ClipboardList className="h-4 w-4" />,
                onClick: () =>
                  nav({
                    to: "/app/checklists/execution-history/$executionId",
                    params: { executionId: row.original.id },
                  }),
              },
              {
                label: "ເປີດແບບ Checklist",
                icon: <EyeIcon className="h-4 w-4" />,
                onClick: () =>
                  nav({
                    to: "/app/checklists/$id",
                    params: { id: row.original.checklistId },
                  }),
              },
            ]}
            maxInline={2}
          />
        ),
      },
    ];

  return (
    <div data-tourid="checklist-execution-history-table">
      <DataTable<ChecklistExecutionHistoryRow, unknown>
        noDataMessage="ບໍ່ພົບປະຫວັດການບັນທຶກ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      />
    </div>
  );
}
